import { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '@/theme';
import { paths } from '@/constants';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/primitives';
import { testIds } from '@/test/testIds';

export interface UserMenuProps {
  /** Trigger element (typically the avatar area) — rendered via DropdownMenuTrigger asChild */
  children: React.ReactNode;
  /** Callback when open state changes (used by AppShell for arrow icon) */
  onOpenChange?: (open: boolean) => void;
  /** Whether in mobile view mode */
  isMobile?: boolean;
  /** Callback to open profile modal */
  onOpenProfile: () => void;
  /** Callback to open the org/network context dialog (logo + admin settings) */
  onOpenContextSwitcher?: () => void;
  /** Current context type - shows "Organization" or "Network" label, hidden if null */
  contextType?: 'org' | 'network' | null;
  /** Whether the user can switch orgs/networks — on its own, sufficient to show
   * the menu item even when contextType is null (contextless but switchable). */
  canSwitchContext?: boolean;
  /** Whether to show admin option (superuser only) */
  showAdmin?: boolean;
  /** Whether to show manage option (staff only) */
  showManage?: boolean;
}

interface MenuItemDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  testId?: string;
  dividerAfter?: boolean;
}

export function UserMenu({
  children,
  onOpenChange,
  isMobile: _isMobile = false,
  onOpenProfile,
  onOpenContextSwitcher,
  contextType = null,
  canSwitchContext = false,
  showAdmin = false,
  showManage = false,
}: UserMenuProps) {
  const navigate = useNavigate();

  const items = useMemo<MenuItemDef[]>(() => {
    const result: MenuItemDef[] = [
      {
        key: 'dashboard',
        label: 'Dashboard',
        icon: <Icons.dashboard fontSize="small" />,
        onClick: () => { void navigate(paths.dashboard); },
      },
      {
        key: 'profile',
        label: 'Profile',
        icon: <Icons.profile fontSize="small" />,
        onClick: onOpenProfile,
        testId: testIds.components.userMenu.item('profile'),
      },
    ];

    // canSwitchContext alone is sufficient — a contextless-but-switchable user
    // (no org/network selected yet) still needs a way in. contextType alone
    // also still shows it, for non-switching admins who need org/network
    // logo+name editing (ContextSettingsDialog handles both).
    if ((contextType || canSwitchContext) && onOpenContextSwitcher) {
      result.push({
        key: 'context',
        label: contextType === 'org' ? 'Organization' : contextType === 'network' ? 'Network' : 'Organization/Network',
        icon: contextType === 'org' ? <Icons.organization fontSize="small" /> : <Icons.switchContext fontSize="small" />,
        onClick: onOpenContextSwitcher,
        testId: 'context-menu-item',
      });
    }

    if (showManage) {
      result.push({
        key: 'manage',
        label: 'Manage',
        icon: <Icons.handyman fontSize="small" />,
        onClick: () => { void navigate(paths.manage); },
        testId: 'manage-menu-item',
      });
    }

    if (showAdmin) {
      result.push({
        key: 'admin',
        label: 'Admin',
        icon: <Icons.adminPanelSettings fontSize="small" />,
        onClick: () => { void navigate(paths.admin); },
        testId: 'admin-menu-item',
      });
    }

    const last = result[result.length - 1];
    if (last) last.dividerAfter = true;

    result.push({
      key: 'signout',
      label: 'Sign Out',
      icon: <Icons.signOut fontSize="small" />,
      onClick: () => { void navigate(paths.logout); },
    });

    return result;
  }, [navigate, onOpenProfile, onOpenContextSwitcher, contextType, canSwitchContext, showManage, showAdmin]);

  return (
    <DropdownMenuRoot onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4}>
        {items.map((item) => (
          <Fragment key={item.key}>
            <DropdownMenuItem
              onSelect={item.onClick}
              data-testid={item.testId}
              style={{ gap: 8 }}
            >
              {item.icon}
              {item.label}
            </DropdownMenuItem>
            {item.dividerAfter && <DropdownMenuSeparator />}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
